/**
 * search-app.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shopify × Typesense Product Search
 *
 * Features:
 *  â€?Full-text search with debounce
 *  â€?Collection facet with live search
 *  â€?Dynamic attribute filters per collection (list checkboxes + range sliders)
 *  â€?Active-filter chips with individual remove
 *  â€?Sort control
 *  â€?Pagination with URL state
 *  â€?Mobile sidebar toggle
 *  â€?Shareable filter URLs (all state in query params)
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── Config from Liquid ─────────────────────────────────────────────────────
  const CFG = window.__TS_CONFIG__ || {};
  const PRICE_FIELDS = [
    'price_min',
    'price_max',
    'price_pl_31092080921',
    'price_pl_31092179225',
    'price_pl_31092277529',
    'price_pl_31092310297',
    'price_pl_31092343065',
    'price_pl_31092408601',
    'price_pl_31092441369',
    'price_pl_31092474137',
  ];

  // ── DOM refs ───────────────────────────────────────────────────────────────
  const $ = (id) => document.getElementById(id);
  const EL = {
    searchInput:      $('ts-search-input'),
    clearSearch:      $('ts-clear-search'),
    autocomplete:     $('ts-search-autocomplete'),
    suggestionList:   $('ts-suggestion-list'),
    categorySuggestions: $('ts-category-suggestions'),
    pageSuggestions:  $('ts-page-suggestions'),
    autocompleteProducts: $('ts-autocomplete-products'),
    viewAllSearch:    $('ts-view-all-search'),
    resultCount:      $('ts-result-count'),
    sortSelect:       $('ts-sort-select'),
    viewBtns:         document.querySelectorAll('.ts-view-btn'),
    columnChooser:    $('ts-column-chooser'),
    columnToggle:     $('ts-column-toggle'),
    columnMenu:       $('ts-column-menu'),
    filterToggle:     $('ts-filter-toggle'),
    activeFilters:    $('ts-active-filters'),
    activeFilterBadge:$('ts-active-filter-count'),
    sidebar:          $('ts-sidebar'),
    mobileOverlay:    $('ts-mobile-overlay'),
    clearAllBtn:      $('ts-clear-filters'),
    collectionList:   $('ts-collection-list'),
    collectionSearch: $('ts-collection-search'),
    attributeFilters: $('ts-attribute-filters'),
    productGrid:      $('ts-product-grid'),
    pagination:       $('ts-pagination'),
  };

  // ── State ──────────────────────────────────────────────────────────────────
  let state = {
    query:      '',
    sortBy:     'created_at_ts:desc',
    viewMode:   'grid',
    listSortKey: null,
    listSortDir: 'asc',
    page:       1,
    perPage:    CFG.perPage || 24,
    // Active filters: { [fieldName]: Set<string> | { min, max } }
    filters:    {},
    // Collection-filters config loaded from JSON asset
    filtersConfig: null,
    // Currently active collection handle (drives which attribute filters show)
    activeCollection: null,
    priceListId: null,
    invalidFacetFields: new Set(),
    hiddenListColumns: new Set(),
    hiddenColumnsCollection: null,
  };

  let debounceTimer = null;
  let autocompleteTimer = null;
  let autocompleteRequestId = 0;
  let typesenseClient = null;

  // ── Boot ───────────────────────────────────────────────────────────────────
  async function init() {
    typesenseClient = new Typesense.Client({
      nodes: [{ host: CFG.host, port: CFG.port, protocol: CFG.protocol }],
      apiKey: CFG.apiKey,
      connectionTimeoutSeconds: 5,
    });

    // Load collection â†?attribute config
    state.filtersConfig = await loadFiltersConfig();
    state.priceListId = await loadCustomerPriceListId();
    updatePriceSortControls();
    ensureColumnChooserElements();

    // Restore state from URL
    readURLState();

    // Attach event listeners
    bindEvents();

    // Initial search
    await search();
  }

  // ── Load collection-filters.json ──────────────────────────────────────────
  function ensureColumnChooserElements() {
    if (EL.columnChooser && EL.columnToggle && EL.columnMenu) return true;

    const toolbar = document.querySelector('.ts-toolbar');
    if (!toolbar) return false;

    const chooser = EL.columnChooser || document.createElement('div');
    chooser.id = 'ts-column-chooser';
    chooser.className = 'ts-column-chooser';
    chooser.hidden = true;

    const toggle = EL.columnToggle || document.createElement('button');
    toggle.id = 'ts-column-toggle';
    toggle.type = 'button';
    toggle.className = 'ts-column-toggle-btn';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'ts-column-menu');
    toggle.textContent = 'Columns';

    const menu = EL.columnMenu || document.createElement('div');
    menu.id = 'ts-column-menu';
    menu.className = 'ts-column-menu';
    menu.hidden = true;

    if (!toggle.parentNode) chooser.appendChild(toggle);
    if (!menu.parentNode) chooser.appendChild(menu);
    if (!chooser.parentNode) toolbar.insertBefore(chooser, EL.filterToggle || null);

    EL.columnChooser = chooser;
    EL.columnToggle = toggle;
    EL.columnMenu = menu;
    return true;
  }

  function hasColumnChooserElements() {
    return Boolean(EL.columnChooser && EL.columnToggle && EL.columnMenu);
  }

  async function loadFiltersConfig() {
    try {
      const res  = await fetch(CFG.filtersUrl);
      const json = await res.json();
      // Strip meta keys
      const config = {};
      for (const [k, v] of Object.entries(json)) {
        if (!k.startsWith('_')) config[k] = v;
      }
      return config;
    } catch (e) {
      console.warn('Could not load collection-filters.json', e);
      return {};
    }
  }

  // ── URL state ─────────────────────────────────────────────────────────────
  async function loadCustomerPriceListId() {
    if (!CFG.isCustomer || !CFG.companyLocationId || !CFG.locationMapUrl) return null;

    try {
      const res = await fetch(CFG.locationMapUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const map = await res.json();
      return map[String(CFG.companyLocationId)] || null;
    } catch (e) {
      console.warn('Could not load B2B location price map', e);
      return null;
    }
  }

  function canShowPrice() {
    return Boolean(CFG.isCustomer && getCustomerPriceField());
  }

  function getCustomerPriceField() {
    // B2C customers use the regular price configured on the Shopify product.
    if (CFG.isB2C) return 'price_min';

    if (!state.priceListId) return null;

    const field = `price_pl_${state.priceListId}`;
    return PRICE_FIELDS.includes(field) ? field : null;
  }

  function isPriceSort(sortBy) {
    return typeof sortBy === 'string' && (
      sortBy.startsWith('price_min:') || sortBy.startsWith('price_pl_')
    );
  }

  function normalizeSortBy(sortBy = state.sortBy) {
    if (!isPriceSort(sortBy)) return sortBy || 'created_at_ts:desc';

    const direction = sortBy.endsWith(':desc') ? 'desc' : 'asc';
    const priceField = getCustomerPriceField();
    return canShowPrice() && priceField ? `${priceField}:${direction}` : 'created_at_ts:desc';
  }

  function getExcludedPriceFields() {
    const allowedPriceField = canShowPrice() ? getCustomerPriceField() : null;
    return PRICE_FIELDS.filter((field) => field !== allowedPriceField).join(',');
  }

  function updatePriceSortControls() {
    const priceField = getCustomerPriceField();
    const priceOptions = Array.from(EL.sortSelect.options).filter((option) =>
      option.value.startsWith('price_min:') || option.value.startsWith('price_pl_')
    );

    priceOptions.forEach((option) => {
      const direction = option.value.endsWith(':desc') ? 'desc' : 'asc';
      option.hidden = !canShowPrice();
      option.disabled = !canShowPrice();
      if (priceField) option.value = `${priceField}:${direction}`;
    });

    state.sortBy = normalizeSortBy(state.sortBy);
    EL.sortSelect.value = state.sortBy;
  }

  function readURLState() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('q'))    state.query  = params.get('q');
    if (params.has('sort')) state.sortBy = params.get('sort');
    state.sortBy = normalizeSortBy(state.sortBy);
    const viewParam = params.get('ts_view') || params.get('view');
    if (['grid', 'list'].includes(viewParam)) {
      state.viewMode = viewParam;
    }
    const listSortParam = params.get('ts_list_sort') || params.get('list_sort');
    if (listSortParam) state.listSortKey = listSortParam;
    const listDirParam = params.get('ts_list_dir') || params.get('list_dir');
    if (['asc', 'desc'].includes(listDirParam)) {
      state.listSortDir = listDirParam;
    }
    if (params.has('page')) state.page   = parseInt(params.get('page')) || 1;
    if (params.has('col'))  state.activeCollection = params.get('col');

    // Restore list filters:  filter[attr_material]=Steel,Brass
    for (const [key, value] of params.entries()) {
      const match = key.match(/^filter\[(.+)\]$/);
      if (match) {
        const field = match[1];
        if (field.endsWith('_range')) {
          const [rawMin = '', rawMax = '', rawUnit = 'mm'] = value.split(',');
          const min = rawMin === '' ? null : Number(rawMin);
          const max = rawMax === '' ? null : Number(rawMax);
          if (Number.isFinite(min) || Number.isFinite(max)) {
            state.filters[field] = {
              min: Number.isFinite(min) ? min : null,
              max: Number.isFinite(max) ? max : null,
              unit: rawUnit === 'in' ? 'in' : 'mm',
            };
          }
        } else {
          state.filters[field] = new Set(value.split(','));
        }
      }
    }

    // Sync UI
    if (state.query)  EL.searchInput.value = state.query;
    if (state.sortBy) EL.sortSelect.value  = state.sortBy;
    updateViewModeUI();
  }

  function writeURLState() {
    const params = new URLSearchParams();
    if (state.query)           params.set('q',    state.query);
    if (state.sortBy !== 'created_at_ts:desc') params.set('sort', state.sortBy);
    if (state.viewMode !== 'grid') params.set('ts_view', state.viewMode);
    if (state.viewMode === 'list' && state.listSortKey) {
      params.set('ts_list_sort', state.listSortKey);
      if (state.listSortDir !== 'asc') params.set('ts_list_dir', state.listSortDir);
    }
    if (state.page > 1)        params.set('page', state.page);
    if (state.activeCollection) params.set('col', state.activeCollection);

    for (const [field, value] of Object.entries(state.filters)) {
      if (value instanceof Set && value.size > 0) {
        params.set(`filter[${field}]`, [...value].join(','));
      } else if (value?.min !== undefined || value?.max !== undefined) {
        params.set(`filter[${field}]`, `${value.min ?? ''},${value.max ?? ''},${value.unit || 'mm'}`);
      }
    }

    const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    history.replaceState(null, '', newUrl);
  }

  // ── Event bindings ────────────────────────────────────────────────────────
  function bindEvents() {
    // Search input
    EL.searchInput.addEventListener('input', () => {
      state.query = EL.searchInput.value.trim();
      EL.clearSearch.hidden = !state.query;
      state.page = 1;
      renderAutocomplete();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => search(), 300);
    });

    EL.searchInput.addEventListener('focus', () => {
      if (EL.searchInput.value.trim()) renderAutocomplete();
    });

    EL.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeAutocomplete();
      } else if (e.key === 'Enter') {
        closeAutocomplete();
        state.query = EL.searchInput.value.trim();
        state.page = 1;
        search();
      }
    });

    EL.clearSearch.addEventListener('click', () => {
      state.query = '';
      EL.searchInput.value = '';
      EL.clearSearch.hidden = true;
      closeAutocomplete();
      state.page = 1;
      search();
    });

    if (EL.viewAllSearch) {
      EL.viewAllSearch.addEventListener('click', () => {
        closeAutocomplete();
        state.query = EL.searchInput.value.trim();
        state.page = 1;
        search();
      });
    }

    document.addEventListener('click', (e) => {
      if (!EL.autocomplete || e.target.closest('.ts-search-bar-wrap')) return;
      closeAutocomplete();
    });

    // Sort
    EL.sortSelect.addEventListener('change', () => {
      state.sortBy = EL.sortSelect.value;
      state.page = 1;
      search();
    });

    EL.viewBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        state.viewMode = btn.dataset.view === 'list' ? 'list' : 'grid';
        updateViewModeUI();
        search();
      });
    });

    if (ensureColumnChooserElements()) {
      EL.columnToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = EL.columnMenu.hidden;
        EL.columnMenu.hidden = !open;
        EL.columnToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      EL.columnMenu.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!e.target.closest('.ts-column-reset-btn')) return;
        state.hiddenListColumns.clear();
        saveHiddenListColumns();
        search();
      });

      EL.columnMenu.addEventListener('change', (e) => {
        const checkbox = e.target.closest('.ts-column-checkbox');
        if (!checkbox) return;
        setColumnHidden(checkbox.value, !checkbox.checked);
      });

      document.addEventListener('click', closeColumnMenu);
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.ts-list-sort-btn');
      if (!btn) return;

      const key = btn.dataset.sortKey;
      if (!key) return;

      if (state.listSortKey === key) {
        state.listSortDir = state.listSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.listSortKey = key;
        state.listSortDir = 'asc';
      }

      state.page = 1;
      search();
    });

    // Clear all filters
    EL.clearAllBtn.addEventListener('click', () => {
      state.filters = {};
      state.activeCollection = null;
      state.page = 1;
      renderAttributeFilters();
      search();
    });

    // Mobile sidebar toggle
    EL.filterToggle.addEventListener('click', () => {
      const open = EL.sidebar.classList.toggle('is-open');
      EL.mobileOverlay.hidden = !open;
    });
    EL.mobileOverlay.addEventListener('click', () => {
      EL.sidebar.classList.remove('is-open');
      EL.mobileOverlay.hidden = true;
    });

    // Collection search filter
    EL.collectionSearch.addEventListener('input', () => {
      const q = EL.collectionSearch.value.toLowerCase();
      EL.collectionList.querySelectorAll('.ts-facet-item').forEach((item) => {
        item.hidden = !item.dataset.label.toLowerCase().includes(q);
      });
    });

    // Accordion toggles (delegated)
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('.ts-filter-group-toggle');
      if (!toggle) return;
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !expanded);
      toggle.nextElementSibling.classList.toggle('is-collapsed', expanded);
    });
  }

  // ── Core search function ──────────────────────────────────────────────────
  async function search() {
    showLoading();
    state.sortBy = normalizeSortBy(state.sortBy);
    writeURLState();

    const filterBy = buildTypesenseFilterBy();
    const facetBy  = buildFacetBy();

    const params = {
      q:                    state.query || '*',
      query_by:             'title,part_no,search_synonyms,collection_titles,vendor,product_type,tags,unit_size',
      query_by_weights:     '5,5,5,4,2,2,1,1',
      sort_by:              state.sortBy,
      exclude_fields:       getExcludedPriceFields(),
      page:                 state.page,
      per_page:             state.perPage,
      facet_by:             facetBy,
      max_facet_values:     200,
      filter_by:            filterBy || undefined,
      highlight_full_fields:'title',
      num_typos:            1,
      typo_tokens_threshold:1,
    };

    try {
      const results = await typesenseClient
        .collections(CFG.collection)
        .documents()
        .search(params);

      renderResults(results);
      renderFacets(await getFilterFacetCounts(results.facet_counts || []));
      renderPagination(results.found, results.page, state.perPage);
      renderActiveFilters();
      updateFilterBadge();
    } catch (err) {
      const missingFacet = getMissingFacetField(err);
      if (missingFacet && !state.invalidFacetFields.has(missingFacet)) {
        state.invalidFacetFields.add(missingFacet);
        delete state.filters[missingFacet];
        delete state.filters[`${missingFacet}_range`];
        await search();
        return;
      }
      showError(err);
    }
  }

  // ── Build filter_by string for Typesense ──────────────────────────────────
  async function renderAutocomplete() {
    if (!EL.autocomplete) return;

    const query = EL.searchInput.value.trim();
    clearTimeout(autocompleteTimer);

    if (!query) {
      closeAutocomplete();
      return;
    }

    openAutocomplete();
    renderSuggestionList(query);
    renderCategorySuggestions(query);
    EL.autocompleteProducts.innerHTML = '<div class="ts-autocomplete-loading">Searching products...</div>';

    autocompleteTimer = setTimeout(() => loadAutocompleteProducts(query), 150);
  }

  function openAutocomplete() {
    EL.autocomplete.hidden = false;
    EL.searchInput.setAttribute('aria-expanded', 'true');
  }

  function closeAutocomplete() {
    if (!EL.autocomplete) return;
    EL.autocomplete.hidden = true;
    EL.searchInput.setAttribute('aria-expanded', 'false');
  }

  function renderSuggestionList(query) {
    const suggestions = buildSuggestions(query);
    EL.suggestionList.innerHTML = suggestions.map((suggestion) => `
      <button type="button" class="ts-suggestion-item" data-query="${escHtml(suggestion)}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <span>${highlightQueryParts(suggestion, query)}</span>
      </button>
    `).join('');

    EL.suggestionList.querySelectorAll('.ts-suggestion-item').forEach((btn) => {
      btn.addEventListener('click', () => {
        EL.searchInput.value = btn.dataset.query;
        state.query = btn.dataset.query;
        state.page = 1;
        closeAutocomplete();
        search();
      });
    });
  }

  function buildSuggestions(query) {
    const normalized = query.toLowerCase();
    const words = normalized.split(/\s+/).filter(Boolean);
    const suggestions = new Set([query]);

    if (words.length === 1) suggestions.add(`${query}s`);
    if (!normalized.includes('stainless')) suggestions.add(`${query} stainless`);
    if (!normalized.includes('nylon')) suggestions.add(`nylon ${query}`);
    if (!normalized.includes('zinc')) suggestions.add(`${query} zinc`);

    return [...suggestions].filter(Boolean).slice(0, 5);
  }

  function renderCategorySuggestions(query) {
    const matches = getMatchingCollections(query).slice(0, 5);

    if (!matches.length) {
      EL.categorySuggestions.innerHTML = '<div class="ts-autocomplete-empty">No categories were found</div>';
      return;
    }

    EL.categorySuggestions.innerHTML = matches.map((collection) => `
      <button type="button" class="ts-category-suggestion" data-handle="${escHtml(collection.handle)}">
        ${highlightQueryParts(collection.label, query)}
      </button>
    `).join('');

    EL.categorySuggestions.querySelectorAll('.ts-category-suggestion').forEach((btn) => {
      btn.addEventListener('click', () => {
        state.activeCollection = btn.dataset.handle;
        state.query = EL.searchInput.value.trim();
        state.page = 1;
        closeAutocomplete();
        search();
      });
    });
  }

  function getMatchingCollections(query) {
    const term = query.toLowerCase();
    return Object.entries(state.filtersConfig || {})
      .map(([handle, config]) => ({
        handle,
        label: config.label || getCollectionLabel(handle),
        haystack: `${handle} ${config.label || ''}`.toLowerCase(),
      }))
      .filter((collection) => fuzzyIncludes(collection.haystack, term))
      .sort((a, b) => scoreCollectionMatch(a, term) - scoreCollectionMatch(b, term) || a.label.localeCompare(b.label));
  }

  function scoreCollectionMatch(collection, term) {
    const label = collection.label.toLowerCase();
    if (label === term) return 0;
    if (label.startsWith(term)) return 1;
    if (collection.handle.startsWith(term.replace(/\s+/g, '-'))) return 2;
    return 3;
  }

  function fuzzyIncludes(haystack, term) {
    if (!term) return false;
    const compactHaystack = haystack.replace(/[-_]+/g, ' ');
    const compactTerm = term.replace(/[-_]+/g, ' ');
    return compactHaystack.includes(compactTerm) || compactTerm.split(/\s+/).every((word) => compactHaystack.includes(word));
  }

  async function loadAutocompleteProducts(query) {
    const requestId = ++autocompleteRequestId;

    const params = {
      q: query || '*',
      query_by: 'title,part_no,search_synonyms,collection_titles,vendor,product_type,tags,unit_size',
      query_by_weights: '5,5,5,4,2,2,1,1',
      sort_by: normalizeSortBy(state.sortBy),
      exclude_fields: getExcludedPriceFields(),
      page: 1,
      per_page: 6,
      highlight_full_fields: 'title,part_no',
      num_typos: 1,
      typo_tokens_threshold: 1,
    };

    try {
      const results = await typesenseClient
        .collections(CFG.collection)
        .documents()
        .search(params);

      if (requestId !== autocompleteRequestId) return;
      renderAutocompleteProducts(results.hits || []);
      const found = results.found || 0;
      EL.viewAllSearch.querySelector('span').textContent = `View all ${found.toLocaleString()} items`;
    } catch (err) {
      if (requestId !== autocompleteRequestId) return;
      console.error('Typesense autocomplete error:', err);
      EL.autocompleteProducts.innerHTML = '<div class="ts-autocomplete-empty">Products could not be loaded</div>';
      EL.viewAllSearch.querySelector('span').textContent = 'View all items';
    }
  }

  function renderAutocompleteProducts(hits) {
    if (!hits.length) {
      EL.autocompleteProducts.innerHTML = '<div class="ts-autocomplete-empty">No products were found</div>';
      return;
    }

    EL.autocompleteProducts.innerHTML = hits.map(({ document: product, highlight }) => `
      <a href="${product.product_url}" class="ts-autocomplete-product">
        <span class="ts-autocomplete-product-image">
          ${product.image_url
            ? `<img src="${product.image_url}" alt="${escHtml(product.title)}" loading="lazy" />`
            : '<span class="ts-autocomplete-product-placeholder"></span>'
          }
        </span>
        <span class="ts-autocomplete-product-info">
          <span class="ts-autocomplete-product-title">${
            highlight?.title?.snippet
              ? highlight.title.snippet.replace(/<mark>/g, '<mark class="ts-highlight">')
              : escHtml(product.title)
          }</span>
          ${product.part_no ? `<span class="ts-autocomplete-part">${escHtml(product.part_no)}</span>` : ''}
          ${product.available === false ? '<span class="ts-autocomplete-stock">Out of stock</span>' : '<span class="ts-autocomplete-stock is-in-stock">In stock</span>'}
        </span>
      </a>
    `).join('');
  }

  function highlightQueryParts(text, query) {
    const escaped = escHtml(text);
    const words = query.split(/\s+/).filter((word) => word.length > 1);
    if (!words.length) return escaped;

    return words.reduce((result, word) => {
      const safeWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return result.replace(new RegExp(`(${safeWord})`, 'ig'), '<strong>$1</strong>');
    }, escaped);
  }
  function buildTypesenseFilterBy(excludedFields = new Set()) {
    const parts = [];

    for (const [field, value] of Object.entries(state.filters)) {
      if (excludedFields.has(field)) continue;
      if (value instanceof Set && value.size > 0) {
        const escaped = [...value].map((v) => `\`${v}\``).join(',');
        parts.push(`${field}:=[${escaped}]`);
      } else if (value?.min !== undefined || value?.max !== undefined) {
        // Range filter â€?the numeric field has _num suffix
        const numField = field.replace(/_range$/, '') + '_num';
        const hasMin = Number.isFinite(value.min);
        const hasMax = Number.isFinite(value.max);
        if (hasMin && hasMax) parts.push(`${numField}:[${value.min}..${value.max}]`);
        else if (hasMin) parts.push(`${numField}:>=${value.min}`);
        else if (hasMax) parts.push(`${numField}:[0..${value.max}]`);
      }
    }

    // Collection filter
    if (state.activeCollection) {
      parts.push(`collections:=[\`${state.activeCollection}\`]`);
    }

    return parts.join(' && ');
  }

  // ── Build facet_by string (standard facets + visible attribute facets) ─────
  function buildFacetBy() {
    const always = ['collections', 'vendor', 'product_type', 'on_sale'];

    // Add attribute facets for the active collection
    const attrFacets = getActiveFacetFields();

    return [...always, ...attrFacets].join(',');
  }

  function getActiveFacetFields() {
    if (!state.filtersConfig || !state.activeCollection) return [];
    const col = state.filtersConfig[state.activeCollection];
    if (!col) return [];
    return col.filters
      .flatMap((filterDef) => getFilterFacetFields(filterDef))
      .filter((field) => !state.invalidFacetFields.has(field));
  }

  function getFilterFacetFields(filterDef) {
    const field = `attr_${filterDef.attribute}`;
    return filterDef.type === 'range' ? [field, `${field}_num`] : [field];
  }

  function getFilterFacetField(filterDef) {
    return getFilterFacetFields(filterDef)[0];
  }

  function getFilterRangeFacetField(filterDef) {
    const field = `attr_${filterDef.attribute}`;
    return `${field}_num`;
  }
  async function getFilterFacetCounts(resultFacetCounts) {
    const col = state.filtersConfig?.[state.activeCollection];
    if (!state.activeCollection || !col?.filters?.length) return resultFacetCounts;

    // The main search response already contains facet counts narrowed by every
    // active filter. Only refresh a facet that is itself selected, excluding
    // that facet's own list/range constraints. This keeps OR-style choices in
    // the current group while selections still narrow every other group.
    const selectedFilters = col.filters.filter((filterDef) => {
      const field = `attr_${filterDef.attribute}`;
      return state.filters[field] || state.filters[`${field}_range`];
    });
    if (!selectedFilters.length) return resultFacetCounts;

    try {
      const facetResults = await Promise.all(selectedFilters.map((filterDef) => {
        const field = `attr_${filterDef.attribute}`;
        const facetFields = getFilterFacetFields(filterDef)
          .filter((facetField) => !state.invalidFacetFields.has(facetField));
        const filterBy = buildTypesenseFilterBy(new Set([field, `${field}_range`]));

        return typesenseClient
          .collections(CFG.collection)
          .documents()
          .search({
            q: state.query || '*',
            query_by: 'title,part_no,search_synonyms,collection_titles,vendor,product_type,tags,unit_size',
            query_by_weights: '5,5,5,4,2,2,1,1',
            page: 1,
            per_page: 1,
            facet_by: facetFields.join(','),
            max_facet_values: 200,
            filter_by: filterBy || undefined,
            num_typos: 1,
            typo_tokens_threshold: 1,
          });
      }));

      const facetsByField = new Map(resultFacetCounts.map((facet) => [facet.field_name, facet]));
      for (const result of facetResults) {
        for (const facet of result.facet_counts || []) facetsByField.set(facet.field_name, facet);
      }
      return [...facetsByField.values()];
    } catch (err) {
      console.warn('Could not load contextual filter facets', err);
      return resultFacetCounts;
    }
  }

  // ── Render results grid ───────────────────────────────────────────────────
  function renderResults(results) {
    const hits  = results.hits || [];
    const found = results.found || 0;

    EL.resultCount.textContent = found === 1 ? '1 product' : `${found.toLocaleString()} products`;
    EL.productGrid.classList.toggle('is-list-view', state.viewMode === 'list');

    if (!hits.length) {
      EL.productGrid.innerHTML = `
        <div class="ts-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="48" height="48">
            <path d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z"/>
          </svg>
          <h3>No products found</h3>
          <p>Try adjusting your search or filters.</p>
        </div>`;
      return;
    }

    if (state.viewMode === 'list') {
      renderListResults(hits);
      return;
    }

    renderGridResults(hits);
  }

  function renderGridResults(hits) {
    EL.productGrid.style.removeProperty('--ts-list-columns');
    EL.productGrid.style.removeProperty('--ts-list-min-width');
    EL.productGrid.innerHTML = hits.map(({ document: p, highlight }) => `
      <a href="${p.product_url}" class="ts-product-card">
        <div class="ts-product-img-wrap">
          ${p.image_url
            ? `<img src="${p.image_url}" alt="${escHtml(p.title)}" loading="lazy" />`
            : `<div class="ts-product-img-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="32" height="32">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
               </div>`
          }
          ${p.on_sale ? '<span class="ts-badge-sale">Sale</span>' : ''}
        </div>
        <div class="ts-product-info">
          ${p.vendor ? `<p class="ts-product-vendor">${escHtml(p.vendor)}</p>` : ''}
          <h3 class="ts-product-title">${
            highlight?.title?.snippet
              ? highlight.title.snippet.replace(/<mark>/g, '<mark class="ts-highlight">')
              : escHtml(p.title)
          }</h3>
          ${renderPartNumberMarkup(p)}
          ${renderPriceMarkup(p)}
          ${p.available === false ? '<p class="ts-out-of-stock">Out of stock</p>' : ''}
        </div>
      </a>
    `).join('');
  }

  function renderListResults(hits) {
    const allColumns = getConfiguredListColumns();
    loadHiddenListColumns();
    const columns = getVisibleListColumns(allColumns);

    if (state.listSortKey && state.listSortKey !== 'title' && !columns.some((col) => `attr_${col.attribute}` === state.listSortKey)) {
      state.listSortKey = null;
      state.listSortDir = 'asc';
    }

    renderColumnMenu(allColumns);
    const listColumns = ['112px', 'minmax(260px, 1.5fr)', ...columns.map(() => 'minmax(140px, 1fr)')].join(' ');
    const listMinWidth = 112 + 260 + (columns.length * 140);
    EL.productGrid.style.setProperty('--ts-list-columns', listColumns);
    EL.productGrid.style.setProperty('--ts-list-min-width', `${Math.max(680, listMinWidth)}px`);
    EL.productGrid.classList.toggle('has-list-attrs', columns.length > 0);

    const sortedHits = sortListHits(hits, columns);
    const header = `
      <div class="ts-list-header" role="row">
        <span class="ts-list-heading" aria-hidden="true"></span>
        ${renderListSortHeading('Product', 'title')}
        ${columns.map((col) => renderListSortHeading(col.label, `attr_${col.attribute}`)).join('')}
      </div>`;

    const rows = sortedHits.map(({ document: p, highlight }) => `
      <a href="${p.product_url}" class="ts-product-card ts-product-list-row">
        <div class="ts-product-img-wrap">
          ${p.image_url
            ? `<img src="${p.image_url}" alt="${escHtml(p.title)}" loading="lazy" />`
            : `<div class="ts-product-img-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="32" height="32">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
               </div>`
          }
          ${p.on_sale ? '<span class="ts-badge-sale">Sale</span>' : ''}
        </div>
        <div class="ts-product-info">
          ${p.vendor ? `<p class="ts-product-vendor">${escHtml(p.vendor)}</p>` : ''}
          <h3 class="ts-product-title">${
            highlight?.title?.snippet
              ? highlight.title.snippet.replace(/<mark>/g, '<mark class="ts-highlight">')
              : escHtml(p.title)
          }</h3>
          ${renderPartNumberMarkup(p)}
          ${renderPriceMarkup(p)}
          ${p.available === false ? '<p class="ts-out-of-stock">Out of stock</p>' : ''}
        </div>
        <div class="ts-list-attrs">
          ${columns.map((col) => `<span class="ts-list-attr" data-label="${escHtml(col.label)}">${escHtml(getAttributeDisplayValue(p, col))}</span>`).join('')}
        </div>
      </a>
    `).join('');

    EL.productGrid.innerHTML = header + rows;
  }

  function getConfiguredListColumns() {
    const colConfig = state.activeCollection ? state.filtersConfig?.[state.activeCollection] : null;
    return colConfig?.filters || [];
  }

  function getVisibleListColumns(columns = getConfiguredListColumns()) {
    return columns.filter((col) => !state.hiddenListColumns.has(col.attribute));
  }

  function getColumnStorageKey() {
    return `ts-search-hidden-list-columns:${state.activeCollection || 'all'}`;
  }

  function loadHiddenListColumns() {
    if (state.hiddenColumnsCollection === state.activeCollection) return;
    state.hiddenColumnsCollection = state.activeCollection;
    try {
      const saved = JSON.parse(localStorage.getItem(getColumnStorageKey()) || "[]");
      state.hiddenListColumns = new Set(Array.isArray(saved) ? saved : []);
    } catch (e) {
      state.hiddenListColumns = new Set();
    }
  }

  function saveHiddenListColumns() {
    localStorage.setItem(getColumnStorageKey(), JSON.stringify([...state.hiddenListColumns]));
  }

  function setColumnHidden(attribute, hidden) {
    if (hidden) state.hiddenListColumns.add(attribute);
    else state.hiddenListColumns.delete(attribute);
    saveHiddenListColumns();
    search();
  }

  function renderColumnMenu(columns) {
    if (!ensureColumnChooserElements()) return;
    const canChooseColumns = state.viewMode === "list" && columns.length > 0;
    EL.columnChooser.hidden = !canChooseColumns;
    if (!canChooseColumns) {
      closeColumnMenu();
      EL.columnMenu.innerHTML = "";
      return;
    }

    EL.columnMenu.innerHTML = `
      <div class="ts-column-menu-header">
        <span class="ts-column-menu-title">Visible columns</span>
        <button type="button" class="ts-column-reset-btn">Show all</button>
      </div>
      ${columns.map((col) => `
        <label class="ts-column-option">
          <input type="checkbox" class="ts-column-checkbox" value="${escHtml(col.attribute)}" ${state.hiddenListColumns.has(col.attribute) ? "" : "checked"} />
          <span>${escHtml(col.label)}</span>
        </label>
      `).join("")}`;
  }

  function closeColumnMenu() {
    if (!hasColumnChooserElements()) return;
    EL.columnMenu.hidden = true;
    EL.columnToggle.setAttribute('aria-expanded', 'false');
  }

  function renderListSortHeading(label, sortKey) {
    const active = state.listSortKey === sortKey;
    const direction = active ? state.listSortDir : 'asc';
    const indicator = active ? (direction === 'asc' ? '^' : 'v') : '';
    return `
      <button type="button" class="ts-list-heading ts-list-sort-btn${active ? ' is-active' : ''}"
              data-sort-key="${escHtml(sortKey)}" aria-sort="${active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}">
        <span>${escHtml(label)}</span>
        <span class="ts-list-sort-icon" aria-hidden="true">${indicator}</span>
      </button>`;
  }

  function sortListHits(hits, columns) {
    const validKeys = new Set(['title', ...columns.map((col) => `attr_${col.attribute}`)]);
    if (!state.listSortKey || !validKeys.has(state.listSortKey)) return hits;

    const direction = state.listSortDir === 'desc' ? -1 : 1;
    return [...hits].sort((a, b) => (
      compareListValues(
        getListSortValue(a.document, state.listSortKey),
        getListSortValue(b.document, state.listSortKey)
      ) * direction
    ));
  }

  function getListSortValue(product, sortKey) {
    if (sortKey === 'title') return product.title || '';
    return getFieldDisplayValue(product, sortKey);
  }

  function compareListValues(a, b) {
    const aMissing = !a || a === '-';
    const bMissing = !b || b === '-';
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;

    const aNum = parseFloat(String(a).replace(/,/g, ''));
    const bNum = parseFloat(String(b).replace(/,/g, ''));
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }

  function getAttributeDisplayValue(product, filterDef) {
    return getFieldDisplayValue(product, `attr_${filterDef.attribute}`);
  }

  function getFieldDisplayValue(product, field) {
    const value = product[field];

    if (Array.isArray(value) && value.length) return value.join(', ');
    if (value !== undefined && value !== null && value !== '') return String(value);

    const numericValue = product[`${field}_num`];
    if (numericValue !== undefined && numericValue !== null && numericValue !== '') return String(numericValue);

    return '-';
  }

  // ── Render facets sidebar ─────────────────────────────────────────────────
  function renderFacets(facetCounts) {
    // Index by field name for quick lookup
    const byField = {};
    for (const fc of facetCounts) byField[fc.field_name] = fc.counts;

    // 1. Collections facet
    renderCollectionFacet(byField['collections'] || []);

    // 2. Attribute filters (dynamic, based on active collection)
    renderAttributeFilters(byField);
  }

  function renderCollectionFacet(counts) {
    const activeCol = state.activeCollection;
    const seen = new Set();

    const configuredCollections = Object.entries(state.filtersConfig || {})
      .map(([handle, config]) => ({
        value: handle,
        count: null,
        label: config.label || getCollectionLabel(handle),
        configuredOnly: true,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const facetCollections = counts.map(({ value, count }) => {
      const label = getCollectionLabel(value);
      seen.add(value);
      return { value, count, label, configuredOnly: false };
    });

    const allCollections = [
      ...facetCollections,
      ...configuredCollections.filter((collection) => !seen.has(collection.value)),
    ];

    if (activeCol && !allCollections.some((collection) => collection.value === activeCol)) {
      allCollections.push({
        value: activeCol,
        count: null,
        label: getCollectionLabel(activeCol),
        configuredOnly: true,
      });
    }

    EL.collectionList.innerHTML = allCollections.map(({ value, count, label, configuredOnly }) => {
      const checked = value === activeCol;
      return `
        <label class="ts-facet-item" data-label="${escHtml(label)}">
          <input type="radio" name="ts-collection" value="${escHtml(value)}"
                 ${checked ? 'checked' : ''} class="ts-facet-radio" />
          <span class="ts-facet-label">${escHtml(label)}</span>
          <span class="ts-facet-count">${count ?? (configuredOnly ? 'Config' : '')}</span>
        </label>`;
    }).join('');

    // Add "All" option at top
    EL.collectionList.insertAdjacentHTML('afterbegin', `
      <label class="ts-facet-item" data-label="All collections">
        <input type="radio" name="ts-collection" value=""
               ${!activeCol ? 'checked' : ''} class="ts-facet-radio" />
        <span class="ts-facet-label">All collections</span>
      </label>
    `);

    const collectionQuery = EL.collectionSearch.value.toLowerCase();
    if (collectionQuery) {
      EL.collectionList.querySelectorAll('.ts-facet-item').forEach((item) => {
        item.hidden = !item.dataset.label.toLowerCase().includes(collectionQuery);
      });
    }

    // Bind collection change
    EL.collectionList.querySelectorAll('.ts-facet-radio').forEach((radio) => {
      radio.addEventListener('change', () => {
        state.activeCollection = radio.value || null;
        // Clear attribute filters when switching collection
        const attrKeys = Object.keys(state.filters).filter((k) => k.startsWith('attr_'));
        attrKeys.forEach((k) => delete state.filters[k]);
        state.page = 1;
        search();
      });
    });
  }

  function renderAttributeFilters(byField = {}) {
    const colHandle = state.activeCollection;
    if (!colHandle || !state.filtersConfig?.[colHandle]) {
      EL.attributeFilters.innerHTML = colHandle
        ? '<p class="ts-no-filters">No filters configured for this collection.</p>'
        : '';
      return;
    }

    const colConfig = state.filtersConfig[colHandle];
    const visibleFilters = colConfig.filters.filter((filterDef) => (
      !state.invalidFacetFields.has(`attr_${filterDef.attribute}`) &&
      !state.invalidFacetFields.has(getFilterFacetField(filterDef)) &&
      (filterDef.type !== 'range' || !state.invalidFacetFields.has(getFilterRangeFacetField(filterDef)))
    ));

    EL.attributeFilters.innerHTML = visibleFilters.map((filterDef) => {
      const field  = `attr_${filterDef.attribute}`;
      const counts = byField[getFilterFacetField(filterDef)] || [];
      const rangeCounts = byField[getFilterRangeFacetField(filterDef)] || [];
      const active = state.filters[field];

      if (filterDef.type === 'range') {
        return renderRangeFilter(filterDef, field, rangeCounts, counts, active);
      }
      return renderListFilter(filterDef, field, counts, active);
    }).join('');

    // Bind checkbox changes
    EL.attributeFilters.querySelectorAll('.ts-facet-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        const field = cb.dataset.field;
        const val   = cb.value;
        if (!state.filters[field]) state.filters[field] = new Set();
        if (cb.checked) {
          state.filters[field].add(val);
        } else {
          state.filters[field].delete(val);
          if (!state.filters[field].size) delete state.filters[field];
        }
        state.page = 1;
        search();
      });
    });

    // Bind range inputs
    EL.attributeFilters.querySelectorAll('.ts-range-apply').forEach((btn) => {
      btn.addEventListener('click', () => {
        const field = btn.dataset.field;
        const group = btn.closest('.ts-range-group');
        const minInput = group.querySelector('.ts-range-min').value.trim();
        const maxInput = group.querySelector('.ts-range-max').value.trim();
        const unit = group.querySelector('.ts-range-unit').value;
        const min = minInput === '' ? null : measurementToMm(Number(minInput), unit);
        const max = maxInput === '' ? null : measurementToMm(Number(maxInput), unit);
        if (Number.isFinite(min) || Number.isFinite(max)) {
          state.filters[field + '_range'] = { min, max, unit };
        } else {
          delete state.filters[field + '_range'];
        }
        state.page = 1;
        search();
      });
    });

    EL.attributeFilters.querySelectorAll('.ts-range-unit').forEach((select) => {
      select.addEventListener('change', () => updateRangeInputLabels(select.closest('.ts-range-group')));
    });
  }

  function renderListFilter(def, field, counts, active) {
    if (!counts.length) return '';
    const items = counts.map(({ value, count }) => {
      const checked = active instanceof Set && active.has(value);
      return `
        <label class="ts-facet-item">
          <input type="checkbox" class="ts-facet-checkbox"
                 data-field="${field}" value="${escHtml(value)}"
                 ${checked ? 'checked' : ''} />
          <span class="ts-facet-label">${escHtml(value)}</span>
          <span class="ts-facet-count">${count}</span>
        </label>`;
    }).join('');

    return `
      <div class="ts-filter-group">
        <button class="ts-filter-group-toggle" aria-expanded="true">
          ${escHtml(def.label)}
          <svg class="ts-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        <div class="ts-filter-group-body">
          <div class="ts-facet-list">${items}</div>
        </div>
      </div>`;
  }

  function sortMeasurementValueCounts(counts) {
    return [...counts].sort((a, b) => {
      const aNum = parseMeasurementToMm(a.value);
      const bNum = parseMeasurementToMm(b.value);
      const aHasNum = aNum !== null;
      const bHasNum = bNum !== null;

      if (aHasNum && bHasNum && aNum !== bNum) return aNum - bNum;
      if (aHasNum && !bHasNum) return -1;
      if (!aHasNum && bHasNum) return 1;
      return String(a.value).localeCompare(String(b.value), undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  function parseMeasurementToMm(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return null;

    const unit = detectMeasurementUnit(raw);
    if (!unit) return null;

    const match = raw.match(/(\d+(?:\.\d+)?)\s*[- ]\s*(\d+)\s*\/\s*(\d+)|(\d+)\s*\/\s*(\d+)|\d+(?:\.\d+)?/);
    if (!match) return null;

    let number;
    if (match[1] !== undefined && match[2] !== undefined && match[3] !== undefined) {
      const whole = parseFloat(match[1]);
      const numerator = parseFloat(match[2]);
      const denominator = parseFloat(match[3]);
      if (!denominator) return null;
      number = whole + numerator / denominator;
    } else if (match[4] !== undefined && match[5] !== undefined) {
      const numerator = parseFloat(match[4]);
      const denominator = parseFloat(match[5]);
      if (!denominator) return null;
      number = numerator / denominator;
    } else {
      number = parseFloat(match[0]);
    }

    if (!Number.isFinite(number)) return null;
    const mm = unit === 'in' ? number * 25.4 : number;
    return Math.round(mm * 10000) / 10000;
  }

  function detectMeasurementUnit(value) {
    if (/(^|[\d\s])(?:mm|millimeter|millimeters)\b/.test(value)) return 'mm';
    if (/(^|[\d\s])(?:in|inch|inches)\b|"/.test(value)) return 'in';
    return null;
  }

  function measurementToMm(value, unit) {
    if (!Number.isFinite(value)) return null;
    const mm = unit === 'in' ? value * 25.4 : value;
    return Math.round(mm * 10000) / 10000;
  }

  function measurementFromMm(value, unit) {
    if (!Number.isFinite(value)) return '';
    const converted = unit === 'in' ? value / 25.4 : value;
    return Math.round(converted * 10000) / 10000;
  }

  function updateRangeInputLabels(group) {
    if (!group) return;
    const unit = group.querySelector('.ts-range-unit').value;
    group.querySelector('.ts-range-min-label').textContent = `Min (${unit})`;
    group.querySelector('.ts-range-max-label').textContent = `Max (${unit})`;
  }

  function renderRangeFilter(def, field, rangeCounts, valueCounts = [], active) {
    const rangeKey = field + '_range';
    const current  = state.filters[rangeKey];
    const unit = current?.unit === 'in' ? 'in' : 'mm';
    const vals = rangeCounts.map((c) => parseFloat(c.value)).filter((n) => !isNaN(n));
    const globalMin = vals.length ? Math.min(...vals) : 0;
    const globalMax = vals.length ? Math.max(...vals) : 0;
    const items = sortMeasurementValueCounts(valueCounts).map(({ value, count }) => {
      const checked = active instanceof Set && active.has(value);
      return `
        <label class="ts-facet-item">
          <input type="checkbox" class="ts-facet-checkbox"
                 data-field="${field}" value="${escHtml(value)}"
                 ${checked ? 'checked' : ''} />
          <span class="ts-facet-label">${escHtml(value)}</span>
          <span class="ts-facet-count">${count}</span>
        </label>`;
    }).join('');

    return `
      <div class="ts-filter-group">
        <button class="ts-filter-group-toggle" aria-expanded="true">
          ${escHtml(def.label)}
          <svg class="ts-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        <div class="ts-filter-group-body">
          ${items ? `<div class="ts-facet-list">${items}</div>` : ''}
          <div class="ts-range-group">
            <label class="ts-range-unit-field">
              <span>Measurement unit</span>
              <select class="ts-range-unit" aria-label="Measurement unit">
                <option value="mm" ${unit === 'mm' ? 'selected' : ''}>Millimetres (mm)</option>
                <option value="in" ${unit === 'in' ? 'selected' : ''}>Inches (in)</option>
              </select>
            </label>
            <div class="ts-range-inputs">
              <label class="ts-range-input-field">
                <span class="ts-range-min-label">Min (${unit})</span>
                <input type="number" class="ts-range-input ts-range-min"
                       placeholder="Min (${unit})" min="0"
                       value="${measurementFromMm(current?.min, unit)}" />
              </label>
              <span class="ts-range-sep">to</span>
              <label class="ts-range-input-field">
                <span class="ts-range-max-label">Max (${unit})</span>
                <input type="number" class="ts-range-input ts-range-max"
                       placeholder="Max (${unit})" min="0"
                       value="${measurementFromMm(current?.max, unit)}" />
              </label>
            </div>
            <button class="ts-range-apply" data-field="${field}">Apply</button>
            ${current ? `<button class="ts-range-clear" data-field="${rangeKey}">Clear</button>` : ''}
          </div>
        </div>
      </div>`;
  }

  // Active filter chips ───────────────────────────────────────────────────
  function renderActiveFilters() {
    const chips = [];

    if (state.activeCollection) {
      chips.push({ label: `Collection: ${getCollectionLabel(state.activeCollection)}`, clear: () => { state.activeCollection = null; } });
    }

    for (const [field, value] of Object.entries(state.filters)) {
      if (value instanceof Set) {
        for (const v of value) {
          chips.push({ label: `${humanLabel(field)}: ${v}`, clear: () => { value.delete(v); if (!value.size) delete state.filters[field]; } });
        }
      } else if (value?.min !== undefined || value?.max !== undefined) {
        const baseField = field.replace(/_range$/, '');
        const unit = value.unit === 'in' ? 'in' : 'mm';
        const displayMin = measurementFromMm(value.min, unit);
        const displayMax = measurementFromMm(value.max, unit);
        const rangeLabel = Number.isFinite(value.min) && Number.isFinite(value.max)
          ? `${displayMin} to ${displayMax} ${unit}`
          : Number.isFinite(value.min) ? `${displayMin} ${unit} and above` : `0 to ${displayMax} ${unit}`;
        chips.push({ label: `${humanLabel(baseField)}: ${rangeLabel}`, clear: () => { delete state.filters[field]; } });
      }
    }

    EL.activeFilters.hidden = chips.length === 0;
    EL.clearAllBtn.hidden   = chips.length === 0;

    EL.activeFilters.innerHTML = chips.map((chip, i) => `
      <button class="ts-chip" data-index="${i}">
        ${escHtml(chip.label)}
        <span class="ts-chip-remove">&times;</span>
      </button>`).join('');

    EL.activeFilters.querySelectorAll('.ts-chip').forEach((chip, i) => {
      chip.addEventListener('click', () => {
        chips[i].clear();
        state.page = 1;
        search();
      });
    });
  }

  function updateFilterBadge() {
    let count = 0;
    if (state.activeCollection) count++;
    for (const v of Object.values(state.filters)) {
      if (v instanceof Set) count += v.size;
      else if (v?.min !== undefined) count++;
    }
    EL.activeFilterBadge.hidden      = count === 0;
    EL.activeFilterBadge.textContent = count;
  }

  // ── Pagination ────────────────────────────────────────────────────────────
  function updateViewModeUI() {
    EL.productGrid.classList.toggle('is-list-view', state.viewMode === 'list');
    const showColumns = state.viewMode === 'list' && getConfiguredListColumns().length > 0;
    if (ensureColumnChooserElements()) {
      EL.columnChooser.hidden = !showColumns;
      if (!showColumns) closeColumnMenu();
    }
    EL.viewBtns.forEach((btn) => {
      const active = btn.dataset.view === state.viewMode;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function renderPagination(total, currentPage, perPage) {
    const totalPages = Math.ceil(total / perPage);
    if (totalPages <= 1) {
      EL.pagination.hidden = true;
      return;
    }

    EL.pagination.hidden = false;
    const pages = getPaginationRange(currentPage, totalPages);

    EL.pagination.innerHTML = pages.map((p) => {
      if (p === '...') return '<span class="ts-page-ellipsis">...</span>';
      return `<button class="ts-page-btn ${p === currentPage ? 'is-active' : ''}" data-page="${p}">${p}</button>`;
    }).join('');

    // Prev / Next
    EL.pagination.insertAdjacentHTML('afterbegin',
      `<button class="ts-page-btn ts-page-prev" ${currentPage === 1 ? 'disabled' : ''} data-page="${currentPage - 1}">Prev</button>`);
    EL.pagination.insertAdjacentHTML('beforeend',
      `<button class="ts-page-btn ts-page-next" ${currentPage === totalPages ? 'disabled' : ''} data-page="${currentPage + 1}">Next</button>`);

    EL.pagination.querySelectorAll('[data-page]').forEach((btn) => {
      btn.addEventListener('click', () => {
        state.page = parseInt(btn.dataset.page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        search();
      });
    });
  }

  function getPaginationRange(current, total) {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      range.push(i);
    }
    if (range[0] - 1 === 2) range.unshift(2);
    if (total - 1 - range[range.length - 1] === 1) range.push(total - 1);

    rangeWithDots.push(1);
    let prev = 0;
    for (const i of range) {
      if (i - prev > 1) rangeWithDots.push('...');
      rangeWithDots.push(i);
      prev = i;
    }
    if (total > 1) {
      if (total - prev > 1) rangeWithDots.push('...');
      rangeWithDots.push(total);
    }
    return rangeWithDots;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function getCollectionLabel(handle) {
    const col = state.filtersConfig?.[handle];
    return col?.label || handle.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function humanLabel(field) {
    // attr_material -> Material
    return field
      .replace(/^attr_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function renderPartNumberMarkup(product) {
    return product.part_no
      ? `<div class="ts-product-part-number"><span>Part #</span><strong>${escHtml(product.part_no)}</strong></div>`
      : '';
  }

  function renderPriceMarkup(product) {
    const price = getDisplayPrice(product);
    if (!price) return '';

    const packageSize = product.unit_size
      ? `<span class="ts-product-package-size"><span>Package</span><strong>${escHtml(product.unit_size)}</strong></span>`
      : '';

    return `<div class="ts-product-price"><strong class="ts-product-price-value">${price}</strong>${packageSize}</div>`;
  }

  function getDisplayPrice(product) {
    const priceField = getCustomerPriceField();
    if (!canShowPrice() || !priceField) return '';

    const catalogPrice = product[priceField];
    if (catalogPrice === undefined || catalogPrice === null || catalogPrice === '') return '';

    return formatPrice(catalogPrice, catalogPrice);
  }

  function formatPrice(min, max) {
    const sym = CFG.currency || '$';
    const fmt = (n) => `${sym}${parseFloat(n).toFixed(2)}`;
    if (!min && !max) return '';
    if (min === max)  return fmt(min);
    return `${fmt(min)} - ${fmt(max)}`;
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getMissingFacetField(err) {
    const message = String(err?.message || err?.body || '');
    const match = message.match(/facet field named `([^`]+)`/i);
    return match ? match[1] : null;
  }

  function showLoading() {
    EL.productGrid.innerHTML = `
      <div class="ts-loading">
        <div class="ts-spinner"></div>
      </div>`;
  }

  function showError(err) {
    console.error('Typesense search error:', err);
    EL.productGrid.innerHTML = `
      <div class="ts-empty">
        <h3>Something went wrong</h3>
        <p>Could not load products. Please try again.</p>
      </div>`;
  }

  // ── Range clear buttons (delegated) ───────────────────────────────────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.ts-range-clear');
    if (!btn) return;
    delete state.filters[btn.dataset.field];
    state.page = 1;
    search();
  });

  // ── Start ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
